/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/**
 * Base component for legal pages with consistent styling
 */
abstract class LegalPage(
    private val onBackClick: () -> Unit,
    private val onNavigate: ((String) -> Unit)? = null,
    private val currentPage: String = "",
    private val isLoggedIn: Boolean = false,
    private val onAppNavigate: ((String) -> Unit)? = null,
) {
    abstract val title: String
    abstract val lastUpdated: String
    abstract fun buildContent(): String

    private val isMobile = ResponsiveUtils.isMobile()

    fun render(): HTMLElement {
        // Scroll to top when rendering legal pages
        window.scrollTo(0.0, 0.0)

        val container = document.createElement("div") as HTMLElement
        val t = WebTheme.current
        container.style.cssText = """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            background: ${if (t.isDark) "transparent" else t.background};
            color: ${if (t.isDark) "#ffffff" else t.textPrimary};
            min-height: 100vh;
        """
        container.innerHTML = buildPageHTML()
        return container
    }

    private fun buildPageHTML(): String {
        val t = WebTheme.current
        val textMuted = if (t.isDark) "#666" else t.textSecondary
        val textBody = if (t.isDark) "#d4d4d4" else t.textPrimary
        val backLinkColor = if (t.isDark) "#00d9ff" else t.accentStrong
        val backText = if (i18n.LocaleManager.getCurrentLocale().code == "fr") "‹ Retour" else "‹ Back"

        val barsOpacity = if (t.isDark) "" else "opacity: 0.3; filter: brightness(1.5);"
        val barsHtml = if (isMobile) "" else """
                <!-- 3-bars animated container (fixed position, scroll-driven) -->
                <div id="bars-container" style="position: fixed; left: 0; top: 10px; z-index: 1; pointer-events: none; transition: none; $barsOpacity">
                    <img id="bars-3" src="/team-3-bars.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 0;">
                    <img id="bars-frame" src="/team-bars-wizer-frame.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 1;">
                    <img id="bars-wizer" src="/team-wizer-only.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 1;">
                </div>
        """

        val contentPadding = if (isMobile) "60px 20px 0" else "80px 48px 0"
        val titlePaddingLeft = if (isMobile) "0" else "440px"
        val titleSize = if (isMobile) "32px" else "40px"

        val navHtml = if (!isMobile && onNavigate != null) {
            MarketingMenu.hamburgerButtonHtml() + MarketingMenu.overlayHtml(currentPage = currentPage, isLoggedIn = isLoggedIn)
        } else {
            """<div style="position: fixed; top: 20px; right: 24px; z-index: 100;">
                <a id="back-btn" href="#" style="color: $backLinkColor; text-decoration: none; font-size: 15px; font-weight: 500; cursor: pointer;">$backText</a>
            </div>"""
        }

        val ambientGlow = if (t.isDark) """
            <div style="position: fixed; top: 0; left: 0; right: 0; height: 56px; background: transparent; z-index: 0; pointer-events: none;"></div>
        """ else ""

        return """
            $ambientGlow
            $navHtml

            $barsHtml

            <!-- Content -->
            <div style="position: relative; z-index: 2; padding: $contentPadding; padding-right: 80px;">
                <div style="padding-left: $titlePaddingLeft;">
                    <h1 style="font-size: $titleSize; font-weight: 700; margin-bottom: 16px;">$title</h1>
                    ${if (lastUpdated.isNotEmpty()) """<p style="color: $textMuted; font-size: 14px; margin-bottom: 48px;">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Dernière mise à jour" else "Last updated"}: $lastUpdated</p>""" else ""}

                    <div style="color: $textBody; line-height: 1.8; font-size: 16px;">
                        ${buildContent()}
                    </div>
                </div>
            </div>

            ${MarketingFooter.html(currentPage = currentPage)}
        """.trimIndent()
    }

    open fun attachEventListeners(element: HTMLElement) {
        window.setTimeout({
            if (!isMobile && onNavigate != null) {
                MarketingMenu.attachEventListeners(
                    onNavigate = onNavigate,
                    isLoggedIn = isLoggedIn,
                    onAppNavigate = onAppNavigate,
                )
            } else {
                element.querySelector("#back-btn")?.addEventListener("click", { onBackClick() })
            }
            onNavigate?.let { MarketingFooter.attachEventListeners(it) }
            setupBarsAnimation()
        }, 100)
    }

    private fun setupBarsAnimation() {
        if (isMobile) return
        val barsContainer = document.getElementById("bars-container") as? HTMLElement ?: return
        val bars3 = document.getElementById("bars-3") as? HTMLElement ?: return
        val barsFrame = document.getElementById("bars-frame") as? HTMLElement ?: return
        val barsWizer = document.getElementById("bars-wizer") as? HTMLElement ?: return

        // Start small (0.35) at top-left, grow to full size toward center-left
        val startScale = 0.35
        val endScale = 1.0
        val startTop = 10.0
        val endTop = window.innerHeight / 2.0 - 225

        barsContainer.style.top = "${startTop.toInt()}px"
        barsContainer.style.transform = "scale($startScale)"
        barsContainer.style.transformOrigin = "left top"

        // Start showing wizer+frame, hide bars-3
        bars3.style.opacity = "0"
        barsFrame.style.opacity = "1"
        barsWizer.style.opacity = "1"

        window.addEventListener("scroll", {
            val scrollY = window.scrollY
            val growScroll = 600.0
            val progress = (scrollY / growScroll).coerceIn(0.0, 1.0)

            // Phase 1: grow from small wizer logo to big 3-bars (0..1)
            val scale = startScale + (endScale - startScale) * progress
            val currentTop = startTop + (endTop - startTop) * progress
            barsContainer.style.transform = "scale($scale)"
            barsContainer.style.top = "${currentTop.toInt()}px"
            barsContainer.style.transformOrigin = "left top"

            // Crossfade: wizer/frame fade out, bars-3 fades in
            barsWizer.style.opacity = "${(1.0 - progress * 1.4).coerceIn(0.0, 1.0)}"
            barsFrame.style.opacity = "${(1.0 - progress * 1.2).coerceIn(0.0, 1.0)}"
            bars3.style.opacity = "${((progress - 0.2) * 1.5).coerceIn(0.0, 1.0)}"

            // Phase 2: once at full size, continue drifting down slowly
            // Stop one bar-height (~150px) above the footer
            if (scrollY > growScroll) {
                val extraScroll = scrollY - growScroll
                val drift = extraScroll * 0.15
                val maxTop = window.innerHeight - 450.0 - 50.0
                val cappedTop = (endTop + drift).coerceAtMost(maxTop)
                barsContainer.style.top = "${cappedTop.toInt()}px"
            }
        })
    }
}

/**
 * Terms of Service page
 */
class TermsPage(onBackClick: () -> Unit, onNavigate: ((String) -> Unit)? = null, currentPage: String = "terms", isLoggedIn: Boolean = false, onAppNavigate: ((String) -> Unit)? = null) : LegalPage(onBackClick, onNavigate, currentPage, isLoggedIn, onAppNavigate) {
    private val isFrench = i18n.LocaleManager.getCurrentLocale().code == "fr"
    override val title = if (isFrench) "Conditions d'utilisation" else "Terms of Service"
    override val lastUpdated = if (isFrench) "Avril 2026" else "April 2026"

    override fun buildContent(): String {
        if (isFrench) return buildFrenchContent()
        return """
            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 0; margin-bottom: 24px;">1. Agreement to Terms</h2>
            <p style="margin-bottom: 24px;">
                By accessing or using wiz≡r ("the Service"), you agree to be bound by these Terms of Service.
                If you disagree with any part of these terms, you do not have permission to access the Service.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">2. Description of Service</h2>
            <p style="margin-bottom: 24px;">
                wiz≡r is a personal finance management application that helps you track your savings rate,
                monitor spending, and build better financial habits. The Service connects to your financial
                institutions through Plaid, a third-party service provider, to aggregate your financial data.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">3. Account Registration</h2>
            <p style="margin-bottom: 24px;">
                To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Provide accurate and complete information</li>
                <li style="margin-bottom: 8px;">Maintain and update your information to keep it accurate</li>
                <li style="margin-bottom: 8px;">Maintain the security and confidentiality of your account credentials</li>
                <li style="margin-bottom: 8px;">Accept responsibility for all activities under your account</li>
                <li style="margin-bottom: 8px;">Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">4. Subscription and Billing</h2>
            <p style="margin-bottom: 24px;">
                wiz≡r offers the following subscription tiers:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Standard ($6.99 CAD/month or $72 CAD/year):</strong> Unlimited accounts, AI categorization, savings insights</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Pro ($12.99 CAD/month or $132 CAD/year):</strong> All Standard features plus multi-select batch actions, transaction notifications, split & distribute transactions, customizable grid dashboard, and context filtering</li>
            </ul>
            <p style="margin-bottom: 24px;">
                Subscriptions are billed monthly or yearly, depending on your chosen plan, and can be
                cancelled at any time. Cancellation will take effect at the end of your current billing period.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">5. Prohibited Uses</h2>
            <p style="margin-bottom: 24px;">
                You agree not to use the Service to:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Violate any applicable laws or regulations</li>
                <li style="margin-bottom: 8px;">Attempt to access accounts that are not yours</li>
                <li style="margin-bottom: 8px;">Interfere with or disrupt the Service</li>
                <li style="margin-bottom: 8px;">Attempt to reverse engineer, decompile, or disassemble any portion of the Service</li>
                <li style="margin-bottom: 8px;">Use the Service to transmit any malware, viruses, or harmful code</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">6. Disclaimer of Warranties</h2>
            <p style="margin-bottom: 24px;">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
                EXPRESS OR IMPLIED. WIZ3R DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE,
                OR ERROR-FREE.
            </p>
            <p style="margin-bottom: 24px;">
                wiz≡r is not a financial advisor. The information provided through the Service is for
                informational purposes only and should not be considered financial advice. Always consult
                with a qualified financial professional before making financial decisions.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">7. Limitation of Liability</h2>
            <p style="margin-bottom: 24px;">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, WIZ3R SHALL NOT BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO
                YOUR USE OF THE SERVICE.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">8. Changes to Terms</h2>
            <p style="margin-bottom: 24px;">
                We reserve the right to modify these Terms at any time. We will provide notice of significant
                changes by posting the updated Terms on the Service and updating the "Last updated" date.
                Your continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>

        """.trimIndent()
    }

    private fun buildFrenchContent(): String = """
        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 0; margin-bottom: 24px;">1. Acceptation des conditions</h2>
        <p style="margin-bottom: 24px;">
            En accédant ou en utilisant wiz≡r (« le Service »), vous acceptez d'être lié par les présentes Conditions d'utilisation.
            Si vous n'acceptez pas une partie de ces conditions, vous n'êtes pas autorisé à accéder au Service.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">2. Description du Service</h2>
        <p style="margin-bottom: 24px;">
            wiz≡r est une application de gestion des finances personnelles qui vous aide à suivre votre taux d'épargne,
            surveiller vos dépenses et développer de meilleures habitudes financières. Le Service se connecte à vos
            institutions financières par l'intermédiaire de Plaid, un fournisseur de services tiers, pour agréger vos données financières.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">3. Inscription au compte</h2>
        <p style="margin-bottom: 24px;">
            Pour utiliser certaines fonctionnalités du Service, vous devez créer un compte.<br>Vous acceptez de :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Fournir des informations exactes et complètes</li>
            <li style="margin-bottom: 8px;">Maintenir et mettre à jour vos informations pour qu'elles restent exactes</li>
            <li style="margin-bottom: 8px;">Maintenir la sécurité et la confidentialité de vos identifiants de compte</li>
            <li style="margin-bottom: 8px;">Accepter la responsabilité de toutes les activités effectuées sous votre compte</li>
            <li style="margin-bottom: 8px;">Nous informer immédiatement de toute utilisation non autorisée de votre compte</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">4. Abonnement et facturation</h2>
        <p style="margin-bottom: 24px;">
            wiz≡r propose les niveaux d'abonnement suivants :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Standard (6,99 $ CAD/mois ou 72 $ CAD/an) :</strong> Comptes illimités, catégorisation par IA, analyses d'épargne</li>
            <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Pro (12,99 $ CAD/mois ou 132 $ CAD/an) :</strong> Toutes les fonctionnalités Standard plus les actions groupées multi-sélection, les analyses avancées et le support prioritaire</li>
        </ul>
        <p style="margin-bottom: 24px;">
            Les abonnements sont facturés mensuellement ou annuellement, selon le plan choisi, et peuvent être
            annulés à tout moment. L'annulation prendra effet à la fin de votre période de facturation en cours.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">5. Utilisations interdites</h2>
        <p style="margin-bottom: 24px;">
            Vous acceptez de ne pas utiliser le Service pour :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Enfreindre toute loi ou réglementation applicable</li>
            <li style="margin-bottom: 8px;">Tenter d'accéder à des comptes qui ne vous appartiennent pas</li>
            <li style="margin-bottom: 8px;">Interférer avec ou perturber le Service</li>
            <li style="margin-bottom: 8px;">Tenter de rétro-ingéniérer, décompiler ou désassembler toute partie du Service</li>
            <li style="margin-bottom: 8px;">Utiliser le Service pour transmettre tout logiciel malveillant, virus ou code nuisible</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">6. Exclusion de garanties</h2>
        <p style="margin-bottom: 24px;">
            LE SERVICE EST FOURNI « TEL QUEL » ET « TEL QUE DISPONIBLE » SANS GARANTIE D'AUCUNE SORTE,
            EXPRESSE OU IMPLICITE. WIZ3R NE GARANTIT PAS QUE LE SERVICE SERA ININTERROMPU, SÉCURISÉ
            OU EXEMPT D'ERREURS.
        </p>
        <p style="margin-bottom: 24px;">
            wiz≡r n'est pas un conseiller financier. Les informations fournies par le Service sont à titre
            informatif uniquement et ne doivent pas être considérées comme des conseils financiers. Consultez
            toujours un professionnel financier qualifié avant de prendre des décisions financières.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">7. Limitation de responsabilité</h2>
        <p style="margin-bottom: 24px;">
            DANS LA MESURE MAXIMALE PERMISE PAR LA LOI, WIZ3R NE SERA PAS RESPONSABLE DES DOMMAGES
            INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS DÉCOULANT DE OU LIÉS À
            VOTRE UTILISATION DU SERVICE.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">8. Modifications des conditions</h2>
        <p style="margin-bottom: 24px;">
            Nous nous réservons le droit de modifier ces Conditions à tout moment. Nous vous informerons des changements
            importants en publiant les Conditions mises à jour sur le Service et en actualisant la date de « Dernière mise à jour ».
            Votre utilisation continue du Service après les modifications constitue une acceptation des Conditions modifiées.
        </p>

    """.trimIndent()
}

/**
 * Privacy Policy page
 */
class PrivacyPage(onBackClick: () -> Unit, onNavigate: ((String) -> Unit)? = null, currentPage: String = "privacy", isLoggedIn: Boolean = false, onAppNavigate: ((String) -> Unit)? = null) : LegalPage(onBackClick, onNavigate, currentPage, isLoggedIn, onAppNavigate) {
    private val isFrench = i18n.LocaleManager.getCurrentLocale().code == "fr"
    override val title = if (isFrench) "Politique de confidentialité" else "Privacy Policy"
    override val lastUpdated = if (isFrench) "Avril 2026" else "April 2026"

    override fun buildContent(): String {
        if (isFrench) return buildFrenchContent()
        return """
            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 0; margin-bottom: 24px;">Overview</h2>
            <p style="margin-bottom: 24px;">
                At wiz≡r, we take your privacy seriously. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our personal finance application.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Information We Collect</h2>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Personal Information</h3>
            <p style="margin-bottom: 24px;">
                When you register for an account, we collect:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Name and email address</li>
                <li style="margin-bottom: 8px;">Date of birth (optional)</li>
                <li style="margin-bottom: 8px;">Phone number (only if you opt in to SMS notifications)</li>
                <li style="margin-bottom: 8px;">Password (encrypted and hashed &mdash; we never store it in plain text)</li>
            </ul>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Financial Information</h3>
            <p style="margin-bottom: 24px;">
                Through our integration with Plaid, we access:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Account balances</li>
                <li style="margin-bottom: 8px;">Transaction history (merchant, amount, date, category)</li>
                <li style="margin-bottom: 8px;">Account and routing numbers (for account identification only)</li>
            </ul>
            <p style="margin-bottom: 24px;">
                <strong style="color: #33cc33;">We never store your bank login credentials.</strong>
                Authentication is handled entirely by Plaid using bank-level security.
            </p>

            <h3 id="statement-imports" style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Bank Statement Imports (Optional)</h3>
            <p style="margin-bottom: 24px;">
                As an alternative to connecting via Plaid, you may choose to import bank statements
                (CSV or PDF files) directly. When you do:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Your statement file is uploaded to our server for processing</li>
                <li style="margin-bottom: 8px;">PDF statements are processed using Anthropic's AI service to extract transaction data</li>
                <li style="margin-bottom: 8px;">Only the text content of the statement is sent to Anthropic &mdash; no images, passwords, or account credentials</li>
                <li style="margin-bottom: 8px;">Anthropic's API does not use your data for AI model training and retains it only temporarily for abuse prevention</li>
                <li style="margin-bottom: 8px;">The original uploaded file is not stored permanently &mdash; only the extracted transaction data is kept</li>
            </ul>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">AI-Assisted Categorization</h3>
            <p style="margin-bottom: 24px;">
                To provide accurate spending categories, transaction descriptions (merchant name and amount)
                may be processed by Anthropic's AI service. This data is used solely for categorization and
                is not used by Anthropic to train AI models.
            </p>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Usage Data</h3>
            <p style="margin-bottom: 24px;">
                We may collect basic technical information such as error logs for troubleshooting purposes.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">How We Use Your Information</h2>
            <p style="margin-bottom: 24px;">
                We use your information to:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Calculate and display your savings rate and financial metrics</li>
                <li style="margin-bottom: 8px;">Categorize transactions and provide spending insights</li>
                <li style="margin-bottom: 8px;">Generate personalized habit-building advice cards</li>
                <li style="margin-bottom: 8px;">Track progress toward your savings goals</li>
                <li style="margin-bottom: 8px;">Send you alerts and notifications you've opted into</li>
                <li style="margin-bottom: 8px;">Improve and optimize our Service</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Data Sharing and Disclosure</h2>
            <p style="margin-bottom: 24px;">
                <strong style="color: #ff4444;">We do not sell your personal information.</strong>
                We may share data with:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Plaid:</strong> To connect to your financial institutions securely</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Anthropic:</strong> To process bank statement imports and categorize transactions using AI. Anthropic does not use API data for model training.</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Service Providers:</strong> Companies that help us operate (hosting, payment processing)</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Data Security</h2>
            <p style="margin-bottom: 24px;">
                We implement industry-standard security measures:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">256-bit AES encryption for sensitive data at rest</li>
                <li style="margin-bottom: 8px;">TLS encryption for all data in transit</li>
                <li style="margin-bottom: 8px;">SOC 2 Type II compliance (Plaid)</li>
                <li style="margin-bottom: 8px;">Multi-factor authentication options</li>
                <li style="margin-bottom: 8px;">Passwords hashed with BCrypt (never stored in plain text)</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Data Retention</h2>
            <p style="margin-bottom: 24px;">
                We retain your data for as long as your account is active. Transaction history is kept for
                up to 3 years to provide historical analysis. Upon account deletion, we remove your personal
                data within 30 days, except where retention is required by law.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Your Rights</h2>
            <p style="margin-bottom: 24px;">
                Depending on your location, you may have the right to:
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Access and download your personal data</li>
                <li style="margin-bottom: 8px;">Correct inaccurate information</li>
                <li style="margin-bottom: 8px;">Delete your account and data</li>
                <li style="margin-bottom: 8px;">Opt-out of marketing communications</li>
                <li style="margin-bottom: 8px;">Restrict or object to certain processing</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Canadian Privacy Law (PIPEDA)</h2>
            <p style="margin-bottom: 24px;">
                wiz≡r is operated from Canada and complies with the Personal Information Protection and
                Electronic Documents Act (PIPEDA). We collect, use, and disclose personal information only
                for purposes that a reasonable person would consider appropriate under the circumstances.
                You may contact us at any time to access, correct, or delete your personal information.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">California Privacy Rights (CCPA)</h2>
            <p style="margin-bottom: 24px;">
                California residents have additional rights under the CCPA, including the right to know what
                personal information is collected, request deletion, and opt-out of the sale of personal
                information. Since we do not sell personal information, the opt-out right does not apply.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Children's Privacy</h2>
            <p style="margin-bottom: 24px;">
                wiz≡r is not intended for children under 18. We do not knowingly collect personal information
                from children. If you believe a child has provided us with personal information, please contact us.
            </p>

        """.trimIndent()
    }

    private fun buildFrenchContent(): String = """
        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 0; margin-bottom: 24px;">Aperçu</h2>
        <p style="margin-bottom: 24px;">
            Chez wiz≡r, nous prenons votre vie privée au sérieux. Cette Politique de confidentialité explique comment nous collectons, utilisons,
            divulguons et protégeons vos informations lorsque vous utilisez notre application de gestion des finances personnelles.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Informations que nous collectons</h2>

        <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Informations personnelles</h3>
        <p style="margin-bottom: 24px;">
            Lorsque vous créez un compte, nous collectons :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Nom et adresse courriel</li>
            <li style="margin-bottom: 8px;">Date de naissance (facultatif)</li>
            <li style="margin-bottom: 8px;">Numéro de téléphone (uniquement si vous optez pour les notifications par SMS)</li>
            <li style="margin-bottom: 8px;">Mot de passe (chiffré et haché &mdash; nous ne le stockons jamais en clair)</li>
        </ul>

        <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Informations financières</h3>
        <p style="margin-bottom: 24px;">
            Grâce à notre intégration avec Plaid, nous accédons à :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Soldes des comptes</li>
            <li style="margin-bottom: 8px;">Historique des transactions (commerçant, montant, date, catégorie)</li>
            <li style="margin-bottom: 8px;">Numéros de compte et de transit (uniquement pour l'identification du compte)</li>
        </ul>
        <p style="margin-bottom: 24px;">
            <strong style="color: #33cc33;">Nous ne stockons jamais vos identifiants bancaires.</strong>
            L'authentification est gérée entièrement par Plaid avec une sécurité de niveau bancaire.
        </p>

        <h3 id="statement-imports" style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Importation de relevés bancaires (facultatif)</h3>
        <p style="margin-bottom: 24px;">
            En alternative à la connexion via Plaid, vous pouvez choisir d'importer des relevés bancaires
            (fichiers CSV ou PDF) directement. Dans ce cas :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Votre fichier de relevé est téléversé sur notre serveur pour traitement</li>
            <li style="margin-bottom: 8px;">Les relevés PDF sont traités à l'aide du service d'IA d'Anthropic pour extraire les données de transaction</li>
            <li style="margin-bottom: 8px;">Seul le contenu textuel du relevé est envoyé à Anthropic &mdash; aucune image, mot de passe ou identifiant de compte</li>
            <li style="margin-bottom: 8px;">L'API d'Anthropic n'utilise pas vos données pour l'entraînement de modèles d'IA et ne les conserve que temporairement pour la prévention des abus</li>
            <li style="margin-bottom: 8px;">Le fichier original téléversé n'est pas stocké de façon permanente &mdash; seules les données de transaction extraites sont conservées</li>
        </ul>

        <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Catégorisation assistée par IA</h3>
        <p style="margin-bottom: 24px;">
            Pour fournir des catégories de dépenses précises, les descriptions de transactions (nom du commerçant et montant)
            peuvent être traitées par le service d'IA d'Anthropic. Ces données sont utilisées uniquement pour la catégorisation et
            ne sont pas utilisées par Anthropic pour entraîner des modèles d'IA.
        </p>

        <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 32px; margin-bottom: 16px;">Données d'utilisation</h3>
        <p style="margin-bottom: 24px;">
            Nous pouvons collecter des informations techniques de base telles que les journaux d'erreurs à des fins de dépannage.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Comment nous utilisons vos informations</h2>
        <p style="margin-bottom: 24px;">
            Nous utilisons vos informations pour :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Calculer et afficher votre taux d'épargne et vos indicateurs financiers</li>
            <li style="margin-bottom: 8px;">Catégoriser les transactions et fournir des analyses de dépenses</li>
            <li style="margin-bottom: 8px;">Générer des conseils personnalisés pour développer de bonnes habitudes</li>
            <li style="margin-bottom: 8px;">Suivre vos progrès vers vos objectifs d'épargne</li>
            <li style="margin-bottom: 8px;">Vous envoyer les alertes et notifications auxquelles vous avez souscrit</li>
            <li style="margin-bottom: 8px;">Améliorer et optimiser notre Service</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Partage et divulgation des données</h2>
        <p style="margin-bottom: 24px;">
            <strong style="color: #ff4444;">Nous ne vendons pas vos informations personnelles.</strong>
            Nous pouvons partager des données avec :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Plaid :</strong> Pour vous connecter à vos institutions financières de manière sécurisée</li>
            <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Anthropic :</strong> Pour traiter les importations de relevés bancaires et catégoriser les transactions à l'aide de l'IA. Anthropic n'utilise pas les données de l'API pour l'entraînement de modèles.</li>
            <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Fournisseurs de services :</strong> Entreprises qui nous aident à opérer (hébergement, traitement des paiements)</li>
            <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Exigences légales :</strong> Lorsque la loi l'exige ou pour protéger nos droits</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Sécurité des données</h2>
        <p style="margin-bottom: 24px;">
            Nous mettons en œuvre des mesures de sécurité conformes aux normes de l'industrie :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Chiffrement AES 256 bits pour les données sensibles au repos</li>
            <li style="margin-bottom: 8px;">Chiffrement TLS pour toutes les données en transit</li>
            <li style="margin-bottom: 8px;">Conformité SOC 2 Type II (Plaid)</li>
            <li style="margin-bottom: 8px;">Options d'authentification multifacteur</li>
            <li style="margin-bottom: 8px;">Mots de passe hachés avec BCrypt (jamais stockés en clair)</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Conservation des données</h2>
        <p style="margin-bottom: 24px;">
            Nous conservons vos données tant que votre compte est actif. L'historique des transactions est conservé jusqu'à
            3 ans pour fournir une analyse historique. Lors de la suppression du compte, nous supprimons vos données
            personnelles dans un délai de 30 jours, sauf lorsque la conservation est requise par la loi.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Vos droits</h2>
        <p style="margin-bottom: 24px;">
            Selon votre lieu de résidence, vous pouvez avoir le droit de :
        </p>
        <ul style="margin-bottom: 24px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Accéder à vos données personnelles et les télécharger</li>
            <li style="margin-bottom: 8px;">Corriger des informations inexactes</li>
            <li style="margin-bottom: 8px;">Supprimer votre compte et vos données</li>
            <li style="margin-bottom: 8px;">Vous désinscrire des communications marketing</li>
            <li style="margin-bottom: 8px;">Restreindre ou vous opposer à certains traitements</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Loi canadienne sur la protection de la vie privée (LPRPDE)</h2>
        <p style="margin-bottom: 24px;">
            wiz≡r est exploité depuis le Canada et se conforme à la Loi sur la protection des renseignements personnels et
            les documents électroniques (LPRPDE). Nous collectons, utilisons et divulguons les renseignements personnels uniquement
            à des fins qu'une personne raisonnable considérerait appropriées dans les circonstances.
            Vous pouvez nous contacter à tout moment pour accéder à vos renseignements personnels, les corriger ou les supprimer.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Droits de confidentialité en Californie (CCPA)</h2>
        <p style="margin-bottom: 24px;">
            Les résidents de la Californie ont des droits supplémentaires en vertu du CCPA, y compris le droit de savoir quelles
            informations personnelles sont collectées, de demander leur suppression et de refuser la vente de renseignements
            personnels. Puisque nous ne vendons pas de renseignements personnels, le droit de refus ne s'applique pas.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Confidentialité des enfants</h2>
        <p style="margin-bottom: 24px;">
            wiz≡r n'est pas destiné aux enfants de moins de 18 ans. Nous ne collectons pas sciemment de renseignements personnels
            auprès d'enfants. Si vous croyez qu'un enfant nous a fourni des renseignements personnels, veuillez nous contacter.
        </p>

    """.trimIndent()
}

/**
 * Security page
 */
class SecurityPage(onBackClick: () -> Unit, onNavigate: ((String) -> Unit)? = null, currentPage: String = "security", isLoggedIn: Boolean = false, onAppNavigate: ((String) -> Unit)? = null) : LegalPage(onBackClick, onNavigate, currentPage, isLoggedIn, onAppNavigate) {
    private val isFrench = i18n.LocaleManager.getCurrentLocale().code == "fr"
    override val title = if (isFrench) "Sécurité" else "Security"
    override val lastUpdated = if (isFrench) "Avril 2026" else "April 2026"

    override fun buildContent(): String {
        if (isFrench) return buildFrenchContent()
        return """
            <p style="margin-bottom: 24px;">
                At wiz≡r, protecting your financial data is our highest priority. This page describes the
                technical and organizational measures we use to keep your information safe.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Authentication & Access Control</h2>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Password Security</h3>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Passwords are hashed using BCrypt with a cost factor of 12</li>
                <li style="margin-bottom: 8px;">We never store or transmit passwords in plain text</li>
                <li style="margin-bottom: 8px;">Passwords are validated against security best practices</li>
            </ul>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Session Management</h3>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Authentication uses JSON Web Tokens (JWT) with HMAC-SHA256 signing</li>
                <li style="margin-bottom: 8px;">Sessions expire after 1 hour of inactivity</li>
                <li style="margin-bottom: 8px;">Tokens are validated on every API request</li>
            </ul>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Multi-Factor Authentication (MFA)</h3>
            <p style="margin-bottom: 16px;">
                wiz≡r supports Time-based One-Time Passwords (TOTP) compatible with Google Authenticator
                and similar apps. MFA adds an additional layer of protection to your account.
            </p>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Least Privilege</h3>
            <p style="margin-bottom: 16px;">
                Users can only access their own data. All API endpoints enforce strict user-level authorization,
                ensuring no user can view or modify another user's financial information.
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Encryption</h2>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Data at Rest</h3>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Sensitive data is encrypted using AES-256-GCM</li>
                <li style="margin-bottom: 8px;">Encryption keys are securely managed and never stored alongside encrypted data</li>
                <li style="margin-bottom: 8px;">On mobile devices, credentials are stored using platform-native secure storage (iOS Keychain, Android EncryptedSharedPreferences)</li>
            </ul>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Data in Transit</h3>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">All communications use TLS 1.2 or higher</li>
                <li style="margin-bottom: 8px;">API endpoints enforce HTTPS-only connections</li>
                <li style="margin-bottom: 8px;">Certificate validation is enforced on all connections</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Plaid Integration Security</h2>
            <p style="margin-bottom: 16px;">
                <strong style="color: #33cc33;">We never see or store your bank login credentials.</strong>
            </p>
            <p style="margin-bottom: 16px;">
                Bank authentication is handled entirely by Plaid, which maintains SOC 2 Type II compliance
                and uses bank-level security.
            </p>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Plaid access tokens are encrypted at rest on our servers</li>
                <li style="margin-bottom: 8px;">Tokens are never exposed to client applications</li>
                <li style="margin-bottom: 8px;">Only the minimum required data products (transactions, accounts) are requested</li>
                <li style="margin-bottom: 8px;">You can revoke bank access at any time by unlinking your accounts</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Data Protection</h2>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Personal financial information is never logged in plain text</li>
                <li style="margin-bottom: 8px;">Database files are excluded from version control</li>
                <li style="margin-bottom: 8px;">Security-relevant code changes are reviewed before deployment</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Security Scanning &amp; Assessment</h2>
            <p style="margin-bottom: 16px;">
                We perform regular automated security assessments using industry-standard tools.
                Our most recent assessment (April 2026) achieved:
            </p>
            <div style="background: ${if (WebTheme.current.isDark) "#111" else WebTheme.current.card}; border: 1px solid ${if (WebTheme.current.isDark) "#1f1f1f" else WebTheme.current.border}; border-radius: 12px; padding: 20px; margin-bottom: 24px; ${if (!WebTheme.current.isDark) "box-shadow: ${WebTheme.current.cardShadow};" else ""}">
                <div style="display: flex; flex-wrap: wrap; gap: 16px; justify-content: center; margin-bottom: 16px;">
                    <div style="text-align: center; padding: 12px 20px;">
                        <div style="font-size: 28px; font-weight: 700; color: ${if (WebTheme.current.isDark) "#33cc33" else "#1B8A3C"};">A+</div>
                        <div style="font-size: 12px; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary};">SSL Labs Grade</div>
                    </div>
                    <div style="text-align: center; padding: 12px 20px;">
                        <div style="font-size: 28px; font-weight: 700; color: ${if (WebTheme.current.isDark) "#33cc33" else "#1B8A3C"};">0</div>
                        <div style="font-size: 12px; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary};">Vulnerabilities Found</div>
                    </div>
                    <div style="text-align: center; padding: 12px 20px;">
                        <div style="font-size: 28px; font-weight: 700; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">58</div>
                        <div style="font-size: 12px; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary};">Security Checks Passed</div>
                    </div>
                    <div style="text-align: center; padding: 12px 20px;">
                        <div style="font-size: 28px; font-weight: 700; color: ${if (WebTheme.current.isDark) "#33cc33" else "#1B8A3C"};">8/8</div>
                        <div style="font-size: 12px; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary};">Security Headers</div>
                    </div>
                </div>
            </div>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">OWASP ZAP:</strong> Automated baseline scans against web application and API — 0 failures</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">SSL/TLS:</strong> A+ grade from Qualys SSL Labs — TLS 1.2+ enforced</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Security Headers:</strong> Full suite (HSTS, CSP, X-Frame-Options, Permissions-Policy, and more)</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">CORS:</strong> Restricted to authorized origins only</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Rate Limiting:</strong> Authentication endpoints protected against brute-force attacks</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Injection/XSS:</strong> SQL injection and cross-site scripting tested and blocked</li>
                <li style="margin-bottom: 8px;"><strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">Cloudflare WAF:</strong> Web Application Firewall with Bot Fight Mode for real-time threat protection</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Incident Response</h2>
            <p style="margin-bottom: 16px;">
                In the event of a security incident, we follow a structured response process:
            </p>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Immediately assess the scope and impact</li>
                <li style="margin-bottom: 8px;">Contain the incident to prevent further damage</li>
                <li style="margin-bottom: 8px;">Preserve evidence for investigation</li>
                <li style="margin-bottom: 8px;">Notify affected users if personal data is compromised</li>
                <li style="margin-bottom: 8px;">Report to Plaid if the incident involves Plaid-related data</li>
                <li style="margin-bottom: 8px;">Document lessons learned and implement preventive measures</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Data Retention & Deletion</h2>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Retention Periods</h3>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">User account data: duration of account + 30 days</li>
                <li style="margin-bottom: 8px;">Financial transactions: up to 7 years (tax/audit compliance)</li>
                <li style="margin-bottom: 8px;">Application logs: 90 days</li>
                <li style="margin-bottom: 8px;">Backup files: 30 days rolling</li>
            </ul>

            <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Account Deletion</h3>
            <p style="margin-bottom: 16px;">
                When you delete your account:
            </p>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Plaid access tokens are revoked via API</li>
                <li style="margin-bottom: 8px;">All personal data is removed from the database</li>
                <li style="margin-bottom: 8px;">Local device storage is cleared</li>
                <li style="margin-bottom: 8px;">Deletion is immediate and irreversible</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Your Security Responsibilities</h2>
            <ul style="margin-bottom: 16px; padding-left: 24px;">
                <li style="margin-bottom: 8px;">Use a strong, unique password for your wiz≡r account</li>
                <li style="margin-bottom: 8px;">Enable multi-factor authentication for added protection</li>
                <li style="margin-bottom: 8px;">Keep your device and operating system up to date</li>
                <li style="margin-bottom: 8px;">Do not share your account credentials with anyone</li>
                <li style="margin-bottom: 8px;">Log out when using shared devices</li>
            </ul>

        """.trimIndent()
    }

    private fun buildFrenchContent(): String = """
        <p style="margin-bottom: 24px;">
            Chez wiz≡r, la protection de vos données financières est notre plus haute priorité. Cette page décrit les mesures techniques et organisationnelles que nous utilisons pour protéger vos informations.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Authentification et contrôle d'accès</h2>
        <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Sécurité des mots de passe</h3>
        <ul style="margin-bottom: 16px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Les mots de passe sont hachés avec BCrypt avec un facteur de coût de 12</li>
            <li style="margin-bottom: 8px;">Nous ne stockons ni ne transmettons jamais les mots de passe en clair</li>
        </ul>
        <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Gestion des sessions</h3>
        <ul style="margin-bottom: 16px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Authentification par jetons JWT signés HMAC-SHA256</li>
            <li style="margin-bottom: 8px;">Les sessions expirent après 1 heure d'inactivité</li>
        </ul>
        <h3 style="font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; margin-top: 24px; margin-bottom: 12px;">Authentification multifacteur (AMF)</h3>
        <p style="margin-bottom: 16px;">
            wiz≡r prend en charge les mots de passe à usage unique basés sur le temps (TOTP), compatibles avec Google Authenticator et applications similaires.
        </p>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Chiffrement</h2>
        <ul style="margin-bottom: 16px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Données au repos : chiffrement AES-256-GCM</li>
            <li style="margin-bottom: 8px;">Données en transit : TLS 1.2 ou supérieur</li>
            <li style="margin-bottom: 8px;">Sur mobile : stockage sécurisé natif (Trousseau iOS, EncryptedSharedPreferences Android)</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Sécurité de l'intégration Plaid</h2>
        <p style="margin-bottom: 16px;">
            <strong style="color: #33cc33;">Nous ne voyons ni ne stockons jamais vos identifiants bancaires.</strong>
        </p>
        <ul style="margin-bottom: 16px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">L'authentification bancaire est gérée entièrement par Plaid (conformité SOC 2 Type II)</li>
            <li style="margin-bottom: 8px;">Les jetons d'accès Plaid sont chiffrés au repos sur nos serveurs</li>
            <li style="margin-bottom: 8px;">Vous pouvez révoquer l'accès bancaire à tout moment en déliant vos comptes</li>
        </ul>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Analyses et évaluation de sécurité</h2>
        <p style="margin-bottom: 16px;">
            Nous effectuons des évaluations de sécurité automatisées régulières. Notre dernière évaluation (avril 2026) :
        </p>
        <div style="background: #111; border: 1px solid #1f1f1f; border-radius: 12px; padding: 20px; margin-bottom: 24px;">
            <div style="display: flex; flex-wrap: wrap; gap: 16px; justify-content: center; margin-bottom: 16px;">
                <div style="text-align: center; padding: 12px 20px;">
                    <div style="font-size: 28px; font-weight: 700; color: #33cc33;">A+</div>
                    <div style="font-size: 12px; color: #888;">Note SSL Labs</div>
                </div>
                <div style="text-align: center; padding: 12px 20px;">
                    <div style="font-size: 28px; font-weight: 700; color: #33cc33;">0</div>
                    <div style="font-size: 12px; color: #888;">Vulnérabilités</div>
                </div>
                <div style="text-align: center; padding: 12px 20px;">
                    <div style="font-size: 28px; font-weight: 700; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">58</div>
                    <div style="font-size: 12px; color: #888;">Tests réussis</div>
                </div>
                <div style="text-align: center; padding: 12px 20px;">
                    <div style="font-size: 28px; font-weight: 700; color: #33cc33;">8/8</div>
                    <div style="font-size: 12px; color: #888;">En-têtes de sécurité</div>
                </div>
            </div>
        </div>

        <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">Vos responsabilités</h2>
        <ul style="margin-bottom: 16px; padding-left: 24px;">
            <li style="margin-bottom: 8px;">Utilisez un mot de passe fort et unique pour votre compte wiz≡r</li>
            <li style="margin-bottom: 8px;">Activez l'authentification multifacteur pour une protection supplémentaire</li>
            <li style="margin-bottom: 8px;">Gardez votre appareil et système d'exploitation à jour</li>
            <li style="margin-bottom: 8px;">Ne partagez jamais vos identifiants</li>
            <li style="margin-bottom: 8px;">Déconnectez-vous sur les appareils partagés</li>
        </ul>
    """.trimIndent()
}

/**
 * About page
 */
class AboutPage(onBackClick: () -> Unit, onNavigate: ((String) -> Unit)? = null, currentPage: String = "about", isLoggedIn: Boolean = false, onAppNavigate: ((String) -> Unit)? = null) : LegalPage(onBackClick, onNavigate, currentPage, isLoggedIn, onAppNavigate) {
    override val title = StringKey.ABOUT_TITLE.tr()
    override val lastUpdated = "April 2026"

    override fun buildContent(): String {
        return """
            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 0; margin-bottom: 24px;">${StringKey.ABOUT_MISSION_TITLE.tr()}</h2>
            <p style="margin-bottom: 24px; font-size: 20px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary};">
                ${StringKey.ABOUT_MISSION.tr()}
            </p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">${StringKey.ABOUT_PHILOSOPHY_TITLE.tr()}</h2>
            <p style="margin-bottom: 24px;">
                <strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">${StringKey.ABOUT_PHILOSOPHY_INTRO.tr()}</strong>
            </p>
            <p style="margin-bottom: 24px; font-size: 18px; color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; font-weight: 500;">
                ${StringKey.ABOUT_SAVINGS_RATE_FORMULA.tr()}
            </p>
            <p style="margin-bottom: 24px;">
                ${StringKey.ABOUT_SAVINGS_RATE_EXPLANATION.tr()}
            </p>

            <div style="
                background: linear-gradient(135deg, rgba(0, 217, 255, 0.1), rgba(51, 204, 51, 0.1));
                border-radius: 16px;
                padding: 32px;
                margin: 48px 0;
                border: 1px solid rgba(0, 217, 255, 0.3);
            ">
                <p style="font-size: 28px; text-align: center; margin: 0; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary};">
                    "${StringKey.ABOUT_QUOTE.tr().replace("\n", "<br>")}"
                </p>
            </div>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">${StringKey.ABOUT_HOW_HELPS_TITLE.tr()}</h2>
            <p style="margin-bottom: 24px;">
                ${StringKey.ABOUT_HOW_HELPS_INTRO.tr()}
            </p>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 12px;">${StringKey.ABOUT_HELP_VISIBILITY.tr()}</li>
                <li style="margin-bottom: 12px;">${StringKey.ABOUT_HELP_INSIGHTS.tr()}</li>
                <li style="margin-bottom: 12px;">${StringKey.ABOUT_HELP_HABITS.tr()}</li>
                <li style="margin-bottom: 12px;">${StringKey.ABOUT_HELP_GOALS.tr()}</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">${StringKey.ABOUT_STORY_TITLE.tr()}</h2>
            <p style="margin-bottom: 24px;">${StringKey.ABOUT_STORY_P1.tr()}</p>
            <p style="margin-bottom: 24px;">${StringKey.ABOUT_STORY_P2.tr()}</p>
            <p style="margin-bottom: 24px;">${StringKey.ABOUT_STORY_P3.tr()}</p>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">${StringKey.ABOUT_VALUES_TITLE.tr()}</h2>
            <ul style="margin-bottom: 24px; padding-left: 24px;">
                <li style="margin-bottom: 16px;">${StringKey.ABOUT_VALUE_SIMPLICITY.tr()}</li>
                <li style="margin-bottom: 16px;">${StringKey.ABOUT_VALUE_PRIVACY.tr()}</li>
                <li style="margin-bottom: 16px;">${StringKey.ABOUT_VALUE_PRICING.tr()}</li>
                <li style="margin-bottom: 16px;">${StringKey.ABOUT_VALUE_RESULTS.tr()}</li>
            </ul>

            <h2 style="font-size: 24px; color: ${if (WebTheme.current.isDark) "#ffffff" else WebTheme.current.textPrimary}; margin-top: 48px; margin-bottom: 24px;">${StringKey.ABOUT_CONTACT_TITLE.tr()}</h2>
            <p style="margin-bottom: 24px;">
                ${StringKey.ABOUT_CONTACT_INTRO.tr()}
            </p>
            <p style="margin-bottom: 24px;">
                <strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Renseignements généraux" else "General Inquiries"} :</strong> social@wiz3r.com<br>
                <strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Soutien technique" else "Support"} :</strong> support@wiz3r.com<br>
                <strong style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"};">${if (i18n.LocaleManager.getCurrentLocale().code == "fr") "Téléphone" else "Phone"} :</strong> <a href="tel:+14387994937" style="color: ${if (WebTheme.current.isDark) "#00d9ff" else "#00838F"}; text-decoration: none;">+1 (438) 799-4937</a>
            </p>
        """.trimIndent()
    }
}

/**
 * Contact page with form
 */
class ContactPage(onBackClick: () -> Unit, onNavigate: ((String) -> Unit)? = null, currentPage: String = "contact", isLoggedIn: Boolean = false, onAppNavigate: ((String) -> Unit)? = null) : LegalPage(onBackClick, onNavigate, currentPage, isLoggedIn, onAppNavigate) {
    private val isFrench = i18n.LocaleManager.getCurrentLocale().code == "fr"
    override val title = if (isFrench) "Nous contacter" else "Contact Us"
    override val lastUpdated = ""

    override fun buildContent(): String {
        if (isFrench) return buildFrenchContent()
        return """
            <p style="margin-bottom: 32px; font-size: 16px;">
                We'd love to hear from you. Fill out the form below and we'll get back to you as soon as possible.
            </p>

            <form id="contact-form" style="display: flex; flex-direction: column; gap: 20px;">
                <div>
                    <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Topic</label>
                    <select id="contact-topic" style="
                        width: 100%;
                        padding: 14px 16px;
                        background: ${if (WebTheme.current.isDark) "#141414" else WebTheme.current.card};
                        color: ${if (WebTheme.current.isDark) "#fff" else WebTheme.current.textPrimary};
                        border: 1px solid ${if (WebTheme.current.isDark) "#333" else WebTheme.current.border};
                        border-radius: 8px;
                        font-size: 15px;
                        appearance: none;
                        -webkit-appearance: none;
                        cursor: pointer;
                    ">
                        <option value="" disabled selected>Select a topic...</option>
                        <option value="account">Account &amp; Login</option>
                        <option value="billing">Billing &amp; Subscription</option>
                        <option value="bank-connection">Bank Connection (Plaid)</option>
                        <option value="statement-import">Statement Import</option>
                        <option value="bug">Bug Report</option>
                        <option value="feature">Feature Request</option>
                        <option value="privacy">Privacy &amp; Data</option>
                        <option value="other">Other</option>
                    </select>
                </div>

                <div id="custom-subject-row" style="display: none;">
                    <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Subject</label>
                    <input id="contact-subject" type="text" placeholder="What's this about?" style="
                        width: 100%;
                        padding: 14px 16px;
                        background: ${if (WebTheme.current.isDark) "#141414" else WebTheme.current.card};
                        color: ${if (WebTheme.current.isDark) "#fff" else WebTheme.current.textPrimary};
                        border: 1px solid ${if (WebTheme.current.isDark) "#333" else WebTheme.current.border};
                        border-radius: 8px;
                        font-size: 15px;
                        box-sizing: border-box;
                    ">
                </div>

                <div>
                    <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Email</label>
                    <input id="contact-email" type="email" placeholder="your@email.com" style="
                        width: 100%;
                        padding: 14px 16px;
                        background: ${if (WebTheme.current.isDark) "#141414" else WebTheme.current.card};
                        color: ${if (WebTheme.current.isDark) "#fff" else WebTheme.current.textPrimary};
                        border: 1px solid ${if (WebTheme.current.isDark) "#333" else WebTheme.current.border};
                        border-radius: 8px;
                        font-size: 15px;
                        box-sizing: border-box;
                    ">
                </div>

                <div>
                    <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Message</label>
                    <textarea id="contact-message" rows="6" placeholder="Tell us what's on your mind..." style="
                        width: 100%;
                        padding: 14px 16px;
                        background: ${if (WebTheme.current.isDark) "#141414" else WebTheme.current.card};
                        color: ${if (WebTheme.current.isDark) "#fff" else WebTheme.current.textPrimary};
                        border: 1px solid ${if (WebTheme.current.isDark) "#333" else WebTheme.current.border};
                        border-radius: 8px;
                        font-size: 15px;
                        resize: vertical;
                        font-family: inherit;
                        box-sizing: border-box;
                    "></textarea>
                </div>

                <button id="contact-submit" type="submit" style="
                    padding: 16px 32px;
                    background: ${if (WebTheme.current.isDark) "linear-gradient(135deg, #00d9ff 0%, #004466 100%)" else "linear-gradient(135deg, ${WebTheme.current.accentStrong} 0%, #006064 100%)"};
                    color: #fff;
                    border: none;
                    border-radius: 12px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: opacity 0.2s;
                ">Send Message</button>

                <p id="contact-status" style="display: none; text-align: center; font-size: 14px; margin-top: 8px;"></p>
            </form>
        """.trimIndent()
    }

    private fun buildFrenchContent(): String = """
        <p style="margin-bottom: 32px; font-size: 16px;">
            Nous aimerions avoir de vos nouvelles. Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
        </p>

        <form id="contact-form" style="display: flex; flex-direction: column; gap: 20px;">
            <div>
                <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Sujet</label>
                <select id="contact-topic" style="
                    width: 100%;
                    padding: 14px 16px;
                    background: #141414;
                    color: #fff;
                    border: 1px solid #333;
                    border-radius: 8px;
                    font-size: 15px;
                    appearance: none;
                    -webkit-appearance: none;
                    cursor: pointer;
                ">
                    <option value="" disabled selected>Choisir un sujet...</option>
                    <option value="account">Compte et connexion</option>
                    <option value="billing">Facturation et abonnement</option>
                    <option value="bank-connection">Connexion bancaire (Plaid)</option>
                    <option value="statement-import">Importation de relevés</option>
                    <option value="bug">Signaler un bogue</option>
                    <option value="feature">Demande de fonctionnalité</option>
                    <option value="privacy">Confidentialité et données</option>
                    <option value="other">Autre</option>
                </select>
            </div>

            <div id="custom-subject-row" style="display: none;">
                <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Sujet</label>
                <input id="contact-subject" type="text" placeholder="De quoi s'agit-il ?" style="
                    width: 100%;
                    padding: 14px 16px;
                    background: #141414;
                    color: #fff;
                    border: 1px solid #333;
                    border-radius: 8px;
                    font-size: 15px;
                    box-sizing: border-box;
                ">
            </div>

            <div>
                <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Courriel</label>
                <input id="contact-email" type="email" placeholder="votre@courriel.com" style="
                    width: 100%;
                    padding: 14px 16px;
                    background: #141414;
                    color: #fff;
                    border: 1px solid #333;
                    border-radius: 8px;
                    font-size: 15px;
                    box-sizing: border-box;
                ">
            </div>

            <div>
                <label style="display: block; color: ${if (WebTheme.current.isDark) "#888" else WebTheme.current.textSecondary}; font-size: 13px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Message</label>
                <textarea id="contact-message" rows="6" placeholder="Dites-nous ce que vous avez en tête..." style="
                    width: 100%;
                    padding: 14px 16px;
                    background: #141414;
                    color: #fff;
                    border: 1px solid #333;
                    border-radius: 8px;
                    font-size: 15px;
                    resize: vertical;
                    font-family: inherit;
                    box-sizing: border-box;
                "></textarea>
            </div>

            <button id="contact-submit" type="submit" style="
                padding: 16px 32px;
                background: linear-gradient(135deg, #00d9ff 0%, #004466 100%);
                color: #fff;
                border: none;
                border-radius: 12px;
                font-size: 16px;
                font-weight: 600;
                cursor: pointer;
                transition: opacity 0.2s;
            ">Envoyer le message</button>

            <p id="contact-status" style="display: none; text-align: center; font-size: 14px; margin-top: 8px;"></p>
        </form>
    """.trimIndent()

    override fun attachEventListeners(element: org.w3c.dom.HTMLElement) {
        super.attachEventListeners(element)

        // Show/hide custom subject field when "Other" is selected
        val topicSelect = element.querySelector("#contact-topic") as? org.w3c.dom.HTMLSelectElement
        val customSubjectRow = element.querySelector("#custom-subject-row") as? org.w3c.dom.HTMLElement
        topicSelect?.addEventListener("change", {
            customSubjectRow?.style?.display = if (topicSelect.value == "other") "block" else "none"
        })

        // Handle form submission — open mailto link
        element.querySelector("#contact-form")?.addEventListener("submit", { e ->
            e.preventDefault()
            val topic = topicSelect?.value ?: ""
            val customSubject = (element.querySelector("#contact-subject") as? org.w3c.dom.HTMLInputElement)?.value ?: ""
            val email = (element.querySelector("#contact-email") as? org.w3c.dom.HTMLInputElement)?.value ?: ""
            val message = (element.querySelector("#contact-message") as? org.w3c.dom.HTMLTextAreaElement)?.value ?: ""
            val status = element.querySelector("#contact-status") as? org.w3c.dom.HTMLElement

            if (topic.isEmpty()) {
                status?.style?.display = "block"
                status?.style?.color = "#ff4444"
                status?.textContent = if (isFrench) "Veuillez choisir un sujet." else "Please select a topic."
                return@addEventListener
            }
            if (email.isEmpty() || !email.contains("@")) {
                status?.style?.display = "block"
                status?.style?.color = "#ff4444"
                status?.textContent = if (isFrench) "Veuillez entrer une adresse courriel valide." else "Please enter a valid email address."
                return@addEventListener
            }
            if (message.isEmpty()) {
                status?.style?.display = "block"
                status?.style?.color = "#ff4444"
                status?.textContent = if (isFrench) "Veuillez entrer un message." else "Please enter a message."
                return@addEventListener
            }

            val topicLabels = if (isFrench) mapOf(
                "account" to "Compte et connexion",
                "billing" to "Facturation et abonnement",
                "bank-connection" to "Connexion bancaire (Plaid)",
                "statement-import" to "Importation de relevés",
                "bug" to "Signalement de bogue",
                "feature" to "Demande de fonctionnalité",
                "privacy" to "Confidentialité et données",
                "other" to customSubject.ifEmpty { "Autre" },
            ) else mapOf(
                "account" to "Account & Login",
                "billing" to "Billing & Subscription",
                "bank-connection" to "Bank Connection (Plaid)",
                "statement-import" to "Statement Import",
                "bug" to "Bug Report",
                "feature" to "Feature Request",
                "privacy" to "Privacy & Data",
                "other" to customSubject.ifEmpty { "Other" },
            )
            val subject = "wiz\u2261r Contact: ${topicLabels[topic] ?: topic}"
            val body = "From: $email\n\n$message"
            val encodedSubject = js("encodeURIComponent")(subject) as String
            val encodedBody = js("encodeURIComponent")(body) as String

            kotlinx.browser.window.location.href = "mailto:support@wiz3r.com?subject=$encodedSubject&body=$encodedBody"

            status?.style?.display = "block"
            status?.style?.color = "#33cc33"
            status?.textContent = if (isFrench) "Ouverture de votre client courriel..." else "Opening your email client..."
        })
    }
}
